import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormGroupDirective,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';

import { Observable, Subject, Subscription, merge } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { CLNSettingsService } from 'src/app/form-config/model/product-settings/cln.settings';
import { ProductCaptureService } from 'src/app/product-capture/services/product-capture.service';
import { ListingVenue } from 'src/app/static-data/models/interfaces/static-data';
import { FormConfigService } from '../../../form-config/form-config.service';
import { Records } from '../../../form-config/model/records.type';
import { LEGAL_JURISDICTIONS } from '../../../shared/consts/legal-juristictions.const';
import { APPLICABLE } from '../../model/consts/applicable.const';
import {
  COUNTRY_OF_OFFER_TO_DEFAULT_TIMEZONE,
  getTimezonesByCountry,
} from '../../model/consts/country-of-offer-to-timezone.const';
import { GOVERNING_LAWS } from '../../model/consts/governing-laws.const';
import {
  FORM_OF_SECURITIES,
  LEGAL_FORM,
} from '../../model/consts/legal-form.const';
import { LEGAL_WRAPPER } from '../../model/consts/legal-wrapper.const';
import { LISTING_STATUS } from '../../model/consts/listing-status.const';
import { PRODUCT_IDENTIFIERS_TYPES } from '../../model/consts/product-identifiers-types.const';
import { YES_NO } from '../../model/consts/yes-no.const';
import { LegalEnum, ProductTypeEnum } from '../../model/enums/product-capture.enums';
import { DropDownOption } from '../../model/interfaces/drop-down-option.interface';
import { IssuerEntity } from '../../model/interfaces/issuer-entity.interface';
import { UnderlyingAsset } from "../../model/interfaces/underlying-asset.interface";
import { calculateBondUnderlyingAsset } from "../../utils/calculate-num-deliverable-underlying-assets";
import { NumberUtils } from "../../utils/number.utils";
import { IncrementalTradingValidator } from './incremental-trading.validator';

@Component({
  selector: 'app-legal-form',
  templateUrl: './legal-form.component.html',
  styleUrls: ['./legal-form.component.scss'],
})
export class LegalFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() formGroupName: string;
  @Input() formName: string;
  @Output() emitAdd: EventEmitter<any> = new EventEmitter();
  @Output() emitDelete: EventEmitter<any> = new EventEmitter();
  @Output() emitCountryOfOfferAdd: EventEmitter<any> = new EventEmitter();
  @Output() emitCountryOfOfferDelete: EventEmitter<any> = new EventEmitter();
  @Input() createOrEdit: 'create-product' | 'edit-product';
  @Input() productId: string;

  form: UntypedFormGroup;
  formOfOfferItems: Records;
  jurisdictionsItems = LEGAL_JURISDICTIONS;
  governingLawsItems = GOVERNING_LAWS;
  labels: Record<string, any>;
  legalFormDropdownItems = LEGAL_FORM;
  productIdentifiersTypes = PRODUCT_IDENTIFIERS_TYPES;
  wrapperDropdownItems = LEGAL_WRAPPER;
  YES_NO = YES_NO;
  formOfSecuritiesItems = FORM_OF_SECURITIES;
  selectedIssuerId: Number;
  issuerEntityItems: DropDownOption[] = [];
  legalProgrammeItems: DropDownOption[] = [];
  issuerEntityData: IssuerEntity[] = [];
  legalProgramme: string;
  private destroy$: Subject<boolean>;
  listingVenueItems = [];
  listingStatusItems = LISTING_STATUS;
  eligibleLiabilityFormatItems = APPLICABLE;
  countriesOfOfferChangesSubscription: Subscription;
  codeType: string;
  generatedCode: string;

  isTradingInUnits = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private formConfigService: FormConfigService,
    private productCaptureService: ProductCaptureService,
    private rootFormGroup: FormGroupDirective,
    private clnSettingsService: CLNSettingsService
  ) {
    this.destroy$ = new Subject();
  }

  ngOnInit(): void {
    this.labels = this.formConfigService.getLabels();
    this.form = this.rootFormGroup.control.get(
      this.formGroupName
    ) as UntypedFormGroup;

    this.formOfOfferItems = this.formConfigService.getOptionsByFieldName(
      'formOfOfferLegal',
      this.formName
    );

    this.configureLegalFormExtraFields();

    this.subscribeToProductIdentifiersChanges(true);

    const productTypeControl = this.form.parent.get('summary.productType');
    productTypeControl.valueChanges
      .pipe(
        startWith(productTypeControl.value),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.clnSettingsService
          .getListingVenueOptions()
          .subscribe((items: ListingVenue[]) => {
            this.listingVenueItems = items.map(
              (item: ListingVenue): DropDownOption => ({
                id: item.id,
                displayValue: item.longName,
              })
            );
          });
        });

    this.setDefaultIssuePrice(
      this.productId,
      this.legal.get('issuePriceLegal').value
    );

    const listingStatusControl = this.legal.get('listingStatus');
    const listingVenueControl = this.legal.get('listingVenue');

    if (listingStatusControl && listingVenueControl) {
      if (listingStatusControl.value === 'UNLISTED') {
        listingVenueControl.disable();
      } else {
        listingVenueControl.enable();
      }

      listingStatusControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((status) => {
          if (status === 'UNLISTED') {
            listingVenueControl.setValue([]);
            listingVenueControl.disable();
          } else {
            listingVenueControl.enable();
          }
        });

      listingVenueControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value: number[]) => {
          if (value?.length && listingStatusControl.value !== 'LISTED') {
            listingStatusControl.setValue('LISTED');
          }
        });
    }

    this.legal
      .get(LegalEnum.tradingInUnits)
      .valueChanges.pipe(
        takeUntil(this.destroy$),
        startWith(this.legal.get(LegalEnum.tradingInUnits).value)
      )
      .subscribe((isTradingInUnits: boolean) =>
        this.onTradingInUnitsChange(isTradingInUnits)
      );

    this.legal
      .get(LegalEnum.denominationLegal)
      .valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(this.legal.get(LegalEnum.denominationLegal).value)
      )
      .subscribe((legalDenominationValue) => this.onLegalDenominationChange(legalDenominationValue))
  }

  ngAfterViewInit(): void {
    const productIdentifiers = this.form.get('productIdentifiers');

    const productIdentifiersArr = productIdentifiers.get(
      'productIdentifiersArr'
    );

    productIdentifiersArr.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (!value) {
          return;
        }

        if (!value.length) {
          this.addRow();
        }
      });

    const countriesOfOfferArr = this.form
      .get('countriesOfOffer')
      .get('countriesOfOfferArr');

    countriesOfOfferArr.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (!value) {
          return;
        }

        if (!value.length) {
          this.addCountriesOfOfferRow();
        }
      });

    this.issuerEntityId.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.selectedIssuerId = value;
        this.updateLegalProgrammeItems();
        this.legal.patchValue({
          legalProgramme: null,
        });
      });

    this.productCaptureService.getIssuers().subscribe((data) => {
      this.issuerEntityData = data;
      this.issuerEntityItems = this.issuerEntityData.map(
        (item) =>
          ({
            id: item.id,
            displayValue: item.entityName,
          } as unknown as DropDownOption)
      );

      this.updateLegalProgrammeItems();
      this.defaultIssuerEntityValue(this.issuerEntityItems, this.createOrEdit);

    
    });
  }

  // Defaullts issue price value for new products, not when editing existing product.
  // This default has been moved out of product-capture-form.const.ts because defaulting
  // number of % values directly in the form model can cause unexpected behaviour with the
  // ThousandSeparator pipe. If the form model has a default value that becomes the saved value,
  // when the UI builds in edit mode the inital default gets put in then patched over without triggering
  // change detection, meaning a value like 1000% will appear in the UI without the pipe applied
  setDefaultIssuePrice(id: string, issuePrice: string | null): void {
    if (id === 'new' && issuePrice === null) {
      this.legal.get('issuePriceLegal').setValue({
        value: '100%',
        minValue: null,
        indicativeValue: null,
        maxValue: null,
      });
    }
  }

  onJurisdictionsLegalChange(indexOfRow: number): void {
    const countryOfOfferRow = this.countriesOfOfferRow.at(indexOfRow);
    const countryOfOffer = countryOfOfferRow.get('jurisdictionsLegal').value;
    const subscription = countryOfOfferRow.get('subscription').value;

    if (!subscription) {
      return;
    }

    const defaultTimezone =
      COUNTRY_OF_OFFER_TO_DEFAULT_TIMEZONE[countryOfOffer];

    const subscriptionRows = countryOfOfferRow.get(
      'subscriptions'
    ) as UntypedFormArray;

    if (subscriptionRows?.length) {
      subscriptionRows.controls.forEach((control) => {
        control.get('timezone').setValue(defaultTimezone);
        control
          .get('timezones')
          .setValue(getTimezonesByCountry(countryOfOffer));
      });
    }
  }

  onSubscriptionChange(indexOfRow: number): void {
    const countryOfOfferRow = this.countriesOfOfferRow.at(indexOfRow);
    const subscription = countryOfOfferRow.get('subscription').value;
    const subscriptions = countryOfOfferRow.get(
      'subscriptions'
    ) as UntypedFormArray;

    if (!subscription) {
      subscriptions.clear();

      return;
    }

    const timezone =
      COUNTRY_OF_OFFER_TO_DEFAULT_TIMEZONE[
        countryOfOfferRow.get('jurisdictionsLegal').value
      ];

    const newSubscription = this.formBuilder.group({
      subStartDate: new UntypedFormControl(null),
      subEndDate: new UntypedFormControl(null),
      isaEndDate: new UntypedFormControl(null),
      timezone: new UntypedFormControl(timezone),
      timezones: new UntypedFormControl(
        getTimezonesByCountry(countryOfOfferRow.get('jurisdictionsLegal').value)
      ),
      distributionChannel: new UntypedFormControl(null),
    });

    subscriptions.push(newSubscription);
  }

  private onLegalDenominationChange(legalDenominationValue: string): void {
    if (
      this.underlyingAssets?.value?.length &&
      this.productType?.value === ProductTypeEnum.REVERSE_CONVERTIBLE_BOND
    ) {
      const updatedAssets = this.underlyingAssets.value.map((asset: UnderlyingAsset) => {
        return {
          ...asset,
          numDeliverableUnderlyingAssets: calculateBondUnderlyingAsset(
            NumberUtils.stringToNumber(asset.initialStrikeDirty?.value),
            NumberUtils.stringToNumber(legalDenominationValue),
            NumberUtils.stringToNumber(asset.denomination)
          )
        }
      })
      this.underlyingAssets.setValue(updatedAssets);
    }
  }

  configureLegalFormExtraFields(): void {
    const {
      [LegalEnum.governingLaws]: governingLawsControl,
      [LegalEnum.incrementalTradingAmount]: incrTrdAmountControl,
      [LegalEnum.incrementalTradingUnits]: incrTrdUnitsControl,
    } = this.legal.controls;

    this.summaryForm.addControl('governingLaws', governingLawsControl);

    const maxTradingAmountValueChanges =
      this.buildMaxTradingValueChangesObservable(
        LegalEnum.maxTradingAmount,
        incrTrdAmountControl
      );
    const maxTradingUnitsValueChanges =
      this.buildMaxTradingValueChangesObservable(
        LegalEnum.maxTradingUnits,
        incrTrdUnitsControl
      );

    const maxTradingValueChanges = merge(
      maxTradingAmountValueChanges,
      maxTradingUnitsValueChanges
    );

    const denominationValuechanges = this.legal
      .get('denominationLegal')
      .valueChanges.pipe(
        takeUntil(this.destroy$),
        map(() =>
          incrTrdAmountControl.enabled
            ? incrTrdAmountControl
            : incrTrdUnitsControl
        )
      );

    merge(maxTradingValueChanges, denominationValuechanges)
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((control: AbstractControl) =>
        control.updateValueAndValidity()
      );
  }

  buildMaxTradingValueChangesObservable(
    type: LegalEnum,
    incrField: AbstractControl
  ): Observable<AbstractControl> {
    return this.legal.get(type).valueChanges.pipe(
      takeUntil(this.destroy$),
      map(() => incrField)
    );
  }

  defaultIssuerEntityValue(
    issuerEntityItems: DropDownOption[],
    createOrEditMode: 'create-product' | 'edit-product'
  ): void {
    // Only defaults if there is a single dropdown option and the form is in 'create' (not 'edit') mode
    if (
      createOrEditMode === 'create-product' &&
      issuerEntityItems.length === 1
    ) {
      const issuerEntityDefaultValue = issuerEntityItems[0].id;
      this.issuerEntityId.setValue(issuerEntityDefaultValue);
    }
  }

  get summaryForm(): UntypedFormGroup {
    return this.rootFormGroup.control.get('summary') as UntypedFormGroup;
  }

  get payoffForm(): UntypedFormGroup {
    return this.rootFormGroup.control.get('payoff') as UntypedFormGroup;
  }

  get legal(): UntypedFormGroup {
    return this.form.get('legal') as UntypedFormGroup;
  }

  get getProductIdentifiersFormGroup(): UntypedFormGroup {
    return this.form.get('productIdentifiers') as UntypedFormGroup;
  }

  get getProductRow(): UntypedFormArray {
    return this.getProductIdentifiersFormGroup.get(
      'productIdentifiersArr'
    ) as UntypedFormArray;
  }

  get underlyingAssets(): AbstractControl {
    return this.rootFormGroup.control.get('summary.underlyingAssetsGroup.underlyingAssets');
  }

  get productType(): AbstractControl {
    return this.rootFormGroup.control.get('summary.productType');
  }

  addRow(): void {
    this.emitAdd.emit();
  }

  deleteRow(index): void {
    this.emitDelete.emit(index);
  }

  get countriesOfOfferRow(): UntypedFormArray {
    return this.form
      .get('countriesOfOffer')
      .get('countriesOfOfferArr') as UntypedFormArray;
  }

  addCountriesOfOfferRow(): void {
    this.emitCountryOfOfferAdd.emit();
  }

  deleteCountriesOfOfferRow(index): void {
    this.emitCountryOfOfferDelete.emit(index);
  }

  addSubscriptionRow(countryOfOfferIndex: number): void {
    const subscriptions = this.countriesOfOfferRow
      .at(countryOfOfferIndex)
      .get('subscriptions') as UntypedFormArray;

    const timezone =
      COUNTRY_OF_OFFER_TO_DEFAULT_TIMEZONE[
        this.countriesOfOfferRow
          .at(countryOfOfferIndex)
          .get('jurisdictionsLegal').value
      ];
    const timezones = getTimezonesByCountry(
      this.countriesOfOfferRow.at(countryOfOfferIndex).get('jurisdictionsLegal')
        .value
    );

    const newSubscription = this.formBuilder.group({
      subStartDate: new UntypedFormControl(null),
      subEndDate: new UntypedFormControl(null),
      isaEndDate: new UntypedFormControl(null),
      timezone: new UntypedFormControl(timezone),
      timezones: new UntypedFormControl(timezones),
      distributionChannel: new UntypedFormControl(null),
    });

    subscriptions.push(newSubscription);
  }

  deleteSubscriptionRow(
    countryOfOfferIndex: number,
    subscriptionIndex: number
  ): void {
    const subscriptions = this.countriesOfOfferRow
      .at(countryOfOfferIndex)
      .get('subscriptions') as UntypedFormArray;

    subscriptions.removeAt(subscriptionIndex);

    if (!subscriptions.length) {
      this.countriesOfOfferRow
        .at(countryOfOfferIndex)
        .get('subscription')
        .setValue(false);
    }
  }

  get issuerEntityId(): UntypedFormControl {
    return this.legal.get('issuerEntityId') as UntypedFormControl;
  }

  private updateLegalProgrammeItems() {
    const selectedIssuer = this.issuerEntityData.find(
      (item) => item.id === this.selectedIssuerId
    );

    this.legalProgrammeItems = selectedIssuer
      ? selectedIssuer.legalProgrammes
          .filter(({ name }) => name)
          .map(
            ({ id, name }) =>
              ({
                id: id,
                displayValue: name,
              } as unknown as DropDownOption)
          )
      : [];
    this.legalProgramme = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private getProductIdentifiersArrControls(): AbstractControl[] {
    return (
      this.getProductIdentifiersFormGroup.get(
        'productIdentifiersArr'
      ) as UntypedFormArray
    ).controls;
  }

  private subscribeToProductIdentifiersChanges(
    subscribeToISINChanges: boolean
  ): void {
    this.updateProductIdentifiers();

    if (subscribeToISINChanges) {
      this.summaryForm
        .get('isin')
        .valueChanges.pipe(
          takeUntil(this.destroy$),
          debounceTime(300),
          map((isin: string | null) => isin?.toUpperCase())
        )
        .subscribe((uppercasedIsin) => {
          this.updateCodeTypeAndGeneratedCode(uppercasedIsin);
          this.subscribeToProductIdentifiersChanges(false);
        });
    }
  }

  private updateProductIdentifiers(): void {
    if (!this.generatedCode) {
      return;
    }

    const productIdentifiersArr = this.getProductIdentifiersArrControls();
    const existingRowIndex = productIdentifiersArr.findIndex((item) => {
      const typeProdIdentifiers = item.get('typeProdIdentifiers').value;
      return typeProdIdentifiers === this.codeType;
    });

    if (existingRowIndex !== -1) {
      productIdentifiersArr[existingRowIndex]
        ?.get('codeProdIdentifiers')
        ?.setValue(this.generatedCode);
    } else {
      const emptyRowIndex = productIdentifiersArr.findIndex((item) => {
        const typeProdIdentifiers = item.get('typeProdIdentifiers').value;
        const codeProdIdentifiers = item.get('codeProdIdentifiers').value;
        return !typeProdIdentifiers && !codeProdIdentifiers;
      });

      if (emptyRowIndex !== -1) {
        productIdentifiersArr[emptyRowIndex].patchValue({
          typeProdIdentifiers: this.codeType,
          codeProdIdentifiers: this.generatedCode,
        });
      } else if (this.codeType || this.generatedCode) {
        const newProductIdentifier = this.formBuilder.group({
          typeProdIdentifiers: [this.codeType],
          codeProdIdentifiers: [this.generatedCode],
        });

        const control = this.getProductIdentifiersFormGroup.get(
          'productIdentifiersArr'
        ) as UntypedFormArray;
        control.push(newProductIdentifier);
      }
    }
  }

  updateProdIdentifierFromISIN(isin: string | null): {
    codeType: string;
    generatedCode: string;
  } {
    if (!isin) {
      return { codeType: null, generatedCode: null };
    }

    const isinPrefix = isin.slice(0, 2);
    const generatedCode = isin.slice(2, 11);
    const generatedCodeWKN = isin.slice(5, 11);

    switch (isinPrefix) {
      case 'CH':
        return { codeType: 'Valoren', generatedCode };
      case 'XS':
        return { codeType: 'Common Code', generatedCode };
      case 'DE':
        return { codeType: 'WKN', generatedCode: generatedCodeWKN };
      case 'US':
        return { codeType: 'CUSIP', generatedCode };
      default:
        return { codeType: null, generatedCode: null };
    }
  }

  private updateCodeTypeAndGeneratedCode(isin: string): void {
    const { codeType, generatedCode } = this.updateProdIdentifierFromISIN(isin);
    this.codeType = codeType;
    this.generatedCode = generatedCode;
  }

  onTradingInUnitsChange(isTradingInUnits: boolean): void {
    this.isTradingInUnits = isTradingInUnits;
    const isTradingInUnitsControl = this.legal.get(LegalEnum.tradingInUnits);

    const { incrementalTradingAmount, incrementalTradingUnits } =
      this.legal.controls;
    const validator = IncrementalTradingValidator(this.legal);

    const oppositeField = isTradingInUnits
      ? incrementalTradingAmount
      : incrementalTradingUnits;
    const oppositeFieldEnum = isTradingInUnits
      ? LegalEnum.incrementalTradingAmount
      : LegalEnum.incrementalTradingUnits;

    const fieldEnum = isTradingInUnits
      ? LegalEnum.incrementalTradingUnits
      : LegalEnum.incrementalTradingAmount;
    const fieldControl = isTradingInUnits
      ? incrementalTradingUnits
      : incrementalTradingAmount;

    oppositeField.setValidators(null);
    oppositeField.setErrors(null);

    this.onIncrementalTradingChange(oppositeFieldEnum);
    fieldControl.setValidators(validator.buildValidator(fieldEnum));

    if (isTradingInUnitsControl.touched) {
      fieldControl.markAsTouched();
      fieldControl.updateValueAndValidity();
    }
  }

  onIncrementalTradingChange(field: LegalEnum): void {
    const oppositeField =
      field === LegalEnum.incrementalTradingAmount
        ? LegalEnum.incrementalTradingUnits
        : LegalEnum.incrementalTradingAmount;
    const control = this.legal.get(oppositeField);
    const value = this.convertIncrementalValueBasedOnField(field);

    if (!value && !this.legal.get(field).touched) {
      return;
    }

    control.setValue(value);
    control.updateValueAndValidity();
  }

  onNumberOfUnitsChange(value: string): void {
    this.legal.updateValueAndValidity();
    this.legal.get(LegalEnum.incrementalTradingUnits).updateValueAndValidity();
    this.legal.get(LegalEnum.maxTradingUnits).updateValueAndValidity();
    this.legal.get(LegalEnum.minTradingUnits).updateValueAndValidity();

    const notional = this.getAmountFromUnits(LegalEnum.numberOfUnits);
    this.legal.get(LegalEnum.notional).setValue(notional);
  }

  onNotionalChange(value: Event): void {
    const numberOfUnits = this.getUnitsFromAmount(LegalEnum.notional);
    this.legal.get(LegalEnum.numberOfUnits).setValue(numberOfUnits);
  }

  onDenominationChange(value: string): void {
    const tradingInUnits = this.legal.get(LegalEnum.tradingInUnits).value;

    if (tradingInUnits) {
      const notional = this.getAmountFromUnits(LegalEnum.numberOfUnits);
      this.legal.get(LegalEnum.notional).setValue(notional);

      const minTradingAmount = this.getAmountFromUnits(
        LegalEnum.minTradingUnits
      );
      this.legal.get(LegalEnum.minTradingAmount).setValue(minTradingAmount);

      const maxTradingAmount = this.getAmountFromUnits(
        LegalEnum.maxTradingUnits
      );
      this.legal.get(LegalEnum.maxTradingAmount).setValue(maxTradingAmount);

      const maxIssueSizeNotional = this.getAmountFromUnits(
        LegalEnum.maxIssueSizeUnits
      );
      this.legal
        .get(LegalEnum.maxIssueSizeNotional)
        .setValue(maxIssueSizeNotional);
    } else {
      const units = this.getUnitsFromAmount(LegalEnum.notional);
      this.legal
        .get(LegalEnum.numberOfUnits)
        .setValue(units || null, { emitEvent: false });

      const minTradingUnits = this.getUnitsFromAmount(
        LegalEnum.minTradingAmount
      );
      this.legal
        .get(LegalEnum.minTradingUnits)
        .setValue(minTradingUnits || null, { emitEvent: false });

      const maxTradingUnits = this.getUnitsFromAmount(
        LegalEnum.maxTradingAmount
      );
      this.legal
        .get(LegalEnum.maxTradingUnits)
        .setValue(maxTradingUnits || null, { emitEvent: false });

      const maxIssueSizeUnits = this.getUnitsFromAmount(
        LegalEnum.maxIssueSizeNotional
      );
      this.legal
        .get(LegalEnum.maxIssueSizeUnits)
        .setValue(maxIssueSizeUnits || null, { emitEvent: false });
    }

    this.activeIncrementalTradingField.updateValueAndValidity();
  }

  onMaxIssueNotionalChange(value: string): void {
    const maxIssueSizeUnits = this.getUnitsFromAmount(
      LegalEnum.maxIssueSizeNotional
    );
    this.legal
      .get(LegalEnum.maxIssueSizeUnits)
      .setValue(maxIssueSizeUnits, { emitEvent: false });
  }

  onMaxIssueUnitsChange(value: string): void {
    const maxIssueSizeNotional = this.getAmountFromUnits(
      LegalEnum.maxIssueSizeUnits
    );
    this.legal
      .get(LegalEnum.maxIssueSizeNotional)
      .setValue(maxIssueSizeNotional, { emitEvent: false });
  }

  onMinTradingAmountChange(value: string): void {
    const minTradingUnits = this.getUnitsFromAmount(LegalEnum.minTradingAmount);
    this.legal
      .get(LegalEnum.minTradingUnits)
      .setValue(minTradingUnits, { emitEvent: false });
  }

  onMinTradingUnitsChange(value: string): void {
    const minTradingAmount = this.getAmountFromUnits(LegalEnum.minTradingUnits);
    this.legal
      .get(LegalEnum.minTradingAmount)
      .setValue(minTradingAmount, { emitEvent: false });
  }

  onMaxTradingAmountChange(value: string): void {
    const maxTradingUnits = this.getUnitsFromAmount(LegalEnum.maxTradingAmount);
    this.legal
      .get(LegalEnum.maxTradingUnits)
      .setValue(maxTradingUnits, { emitEvent: false });
  }

  onMaxTradingUnitsChange(value: string): void {
    const maxTradingAmount = this.getAmountFromUnits(LegalEnum.maxTradingUnits);
    this.legal
      .get(LegalEnum.maxTradingAmount)
      .setValue(maxTradingAmount, { emitEvent: false });
  }

  get activeIncrementalTradingField(): AbstractControl {
    return this.legal.get(LegalEnum.incrementalTradingUnits).enabled
      ? this.legal.get(LegalEnum.incrementalTradingUnits)
      : this.legal.get(LegalEnum.incrementalTradingAmount);
  }

  getUnitsFromAmount(amountField: LegalEnum): number {
    const denomination = Number(
      this.legal.get(LegalEnum.denominationLegal).value
    );
    const amount = Number(this.legal.get(amountField).value);

    if (!denomination || !amount) {
      return null;
    }

    return amount / denomination;
  }

  getAmountFromUnits(unitField: LegalEnum): number {
    const denomination = Number(
      this.legal.get(LegalEnum.denominationLegal).value
    );
    const units = Number(this.legal.get(unitField).value);

    if (!denomination || !units) {
      return null;
    }

    return units * denomination;
  }

  private convertIncrementalValueBasedOnField(field: LegalEnum): string {
    const denomination = Number(
      this.legal.get(LegalEnum.denominationLegal).value
    );

    const incrementalValue = this.legal.get(field).value;

    if (!denomination || !incrementalValue) {
      return null;
    }

    const incremental = Number(incrementalValue);

    const converted =
      field === LegalEnum.incrementalTradingUnits
        ? incremental * denomination
        : incremental / denomination;

    return isNaN(converted) ? null : converted.toString();
  }
}
